import { useRef, useEffect, useMemo } from 'react';
import type { AvatarConfig } from '@flip7/shared';
import { SKIN_PALETTES, HAIR_COLORS } from '@flip7/shared';

interface AvatarProps {
  config?: AvatarConfig | null;
  emoji?: string | null;
  size?: 24 | 36 | 64;
  border?: string | null;
}

// 8x8 pixel art grid (each cell = 1 pixel at base, scaled up)
const GRID = 8;

function configHash(config: AvatarConfig): string {
  return `${config.skin}-${config.hairStyle}-${config.hairColor}-${config.eyes}-${config.mouth}-${config.hat ?? 'n'}-${config.glasses ?? 'n'}-${config.facialHair ?? 'n'}`;
}

const canvasCache = new Map<string, HTMLCanvasElement>();

function renderAvatar(config: AvatarConfig): HTMLCanvasElement {
  const hash = configHash(config);
  const cached = canvasCache.get(hash);
  if (cached) return cached;

  const canvas = document.createElement('canvas');
  canvas.width = GRID;
  canvas.height = GRID;
  const ctx = canvas.getContext('2d')!;

  const skin = SKIN_PALETTES[config.skin] ?? SKIN_PALETTES[0];
  const hairColor = HAIR_COLORS[config.hairColor] ?? HAIR_COLORS[0];

  // Clear
  ctx.clearRect(0, 0, GRID, GRID);

  // Head shape (centered oval-ish) rows 1-6, cols 2-5
  ctx.fillStyle = skin.base;
  // Row 1: cols 3-4 (top of head)
  fillPixels(ctx, [[3, 1], [4, 1]]);
  // Row 2: cols 2-5
  fillPixels(ctx, [[2, 2], [3, 2], [4, 2], [5, 2]]);
  // Row 3-5: cols 2-5 (face)
  for (let y = 3; y <= 5; y++) {
    fillPixels(ctx, [[2, y], [3, y], [4, y], [5, y]]);
  }
  // Row 6: cols 3-4 (chin)
  fillPixels(ctx, [[3, 6], [4, 6]]);

  // Shadow on sides
  ctx.fillStyle = skin.shadow;
  fillPixels(ctx, [[2, 4], [5, 4]]);

  // Hair
  ctx.fillStyle = hairColor;
  drawHair(ctx, config.hairStyle);

  // Eyes
  drawEyes(ctx, config.eyes);

  // Mouth
  drawMouth(ctx, config.mouth, skin.outline);

  // Facial hair
  if (config.facialHair !== null) {
    drawFacialHair(ctx, config.facialHair, hairColor);
  }

  // Hat
  if (config.hat !== null) {
    drawHat(ctx, config.hat);
  }

  // Glasses
  if (config.glasses !== null) {
    drawGlasses(ctx, config.glasses);
  }

  canvasCache.set(hash, canvas);
  return canvas;
}

function fillPixels(ctx: CanvasRenderingContext2D, pixels: [number, number][]) {
  for (const [x, y] of pixels) {
    ctx.fillRect(x, y, 1, 1);
  }
}

function drawHair(ctx: CanvasRenderingContext2D, style: number) {
  switch (style) {
    case 0: // Buzz
      fillPixels(ctx, [[3, 0], [4, 0], [2, 1], [5, 1]]);
      break;
    case 1: // Short
      fillPixels(ctx, [[3, 0], [4, 0], [2, 1], [5, 1], [2, 2]]);
      break;
    case 2: // Medium
      fillPixels(ctx, [[3, 0], [4, 0], [2, 0], [5, 0], [1, 1], [1, 2], [6, 1], [6, 2]]);
      break;
    case 3: // Long
      fillPixels(ctx, [[3, 0], [4, 0], [2, 0], [5, 0], [1, 1], [1, 2], [1, 3], [6, 1], [6, 2], [6, 3]]);
      break;
    case 4: // Curly
      fillPixels(ctx, [[2, 0], [3, 0], [4, 0], [5, 0], [1, 1], [6, 1], [1, 2], [6, 2], [1, 3]]);
      break;
    case 5: // Mohawk
      fillPixels(ctx, [[3, 0], [4, 0], [3, -1 + 1], [4, -1 + 1]]); // top
      ctx.fillRect(3, 0, 2, 1);
      break;
    case 6: // Ponytail
      fillPixels(ctx, [[3, 0], [4, 0], [2, 0], [5, 0], [6, 1], [7, 2], [7, 3]]);
      break;
    case 7: // Pigtails
      fillPixels(ctx, [[3, 0], [4, 0], [2, 0], [5, 0], [1, 1], [6, 1], [0, 2], [0, 3], [7, 2], [7, 3]]);
      break;
    case 8: // Bald
      break; // No hair
    case 9: // Messy
      fillPixels(ctx, [[2, 0], [4, 0], [5, 0], [1, 1], [3, 0], [6, 1], [1, 2]]);
      break;
  }
}

function drawEyes(ctx: CanvasRenderingContext2D, style: number) {
  ctx.fillStyle = '#1a1a1a';
  switch (style) {
    case 0: // Round
      fillPixels(ctx, [[3, 3], [5, 3]]);
      break;
    case 1: // Almond
      fillPixels(ctx, [[3, 3], [5, 3]]);
      ctx.fillStyle = '#ffffff';
      fillPixels(ctx, [[3, 2], [5, 2]]); // highlight
      break;
    case 2: // Wide
      ctx.fillRect(2, 3, 2, 1);
      ctx.fillRect(4, 3, 2, 1);
      break;
    case 3: // Narrow
      fillPixels(ctx, [[3, 3], [5, 3]]);
      break;
    case 4: // Wink
      fillPixels(ctx, [[3, 3]]);
      ctx.fillStyle = '#666';
      fillPixels(ctx, [[5, 3]]);
      break;
    case 5: // Closed
      ctx.fillStyle = '#666';
      fillPixels(ctx, [[3, 3], [5, 3]]);
      break;
  }
}

function drawMouth(ctx: CanvasRenderingContext2D, style: number, outlineColor: string) {
  ctx.fillStyle = outlineColor;
  switch (style) {
    case 0: // Smile
      fillPixels(ctx, [[3, 5], [4, 5]]);
      break;
    case 1: // Neutral
      fillPixels(ctx, [[3, 5], [4, 5]]);
      break;
    case 2: // Open
      ctx.fillStyle = '#8B0000';
      fillPixels(ctx, [[3, 5], [4, 5]]);
      break;
    case 3: // Grin
      fillPixels(ctx, [[3, 5], [4, 5]]);
      ctx.fillStyle = '#ffffff';
      fillPixels(ctx, [[3, 5]]); // tooth
      break;
    case 4: // Smirk
      fillPixels(ctx, [[4, 5]]);
      break;
  }
}

function drawFacialHair(ctx: CanvasRenderingContext2D, style: number, color: string) {
  ctx.fillStyle = color;
  switch (style) {
    case 0: // Stubble
      ctx.globalAlpha = 0.3;
      fillPixels(ctx, [[2, 5], [5, 5], [3, 6], [4, 6]]);
      ctx.globalAlpha = 1;
      break;
    case 1: // Mustache
      fillPixels(ctx, [[2, 4], [3, 4], [4, 4], [5, 4]]);
      break;
    case 2: // Beard
      fillPixels(ctx, [[2, 5], [5, 5], [3, 6], [4, 6], [3, 7], [4, 7]]);
      break;
    case 3: // Goatee
      fillPixels(ctx, [[3, 6], [4, 6], [3, 7]]);
      break;
  }
}

function drawHat(ctx: CanvasRenderingContext2D, style: number) {
  switch (style) {
    case 0: // Cap
      ctx.fillStyle = '#2E4057';
      fillPixels(ctx, [[1, 0], [2, 0], [3, 0], [4, 0], [5, 0], [6, 0], [2, 1], [3, 1], [4, 1], [5, 1]]);
      break;
    case 1: // Beanie
      ctx.fillStyle = '#CC3333';
      fillPixels(ctx, [[2, 0], [3, 0], [4, 0], [5, 0], [3, -1 + 1], [4, -1 + 1]]);
      break;
    case 2: // Top Hat
      ctx.fillStyle = '#1a1a1a';
      fillPixels(ctx, [[2, -1 + 1], [3, -1 + 1], [4, -1 + 1], [5, -1 + 1], [3, 0], [4, 0], [1, 1], [2, 1], [3, 1], [4, 1], [5, 1], [6, 1]]);
      break;
    case 3: // Crown
      ctx.fillStyle = '#FFD700';
      fillPixels(ctx, [[2, 0], [3, 0], [4, 0], [5, 0], [2, 1], [5, 1], [3, 1], [4, 1]]);
      ctx.fillStyle = '#FF0000'; // gem
      fillPixels(ctx, [[3, 0], [4, 0]]);
      break;
    case 4: // Bandana
      ctx.fillStyle = '#CC3333';
      fillPixels(ctx, [[2, 1], [3, 1], [4, 1], [5, 1]]);
      break;
    case 5: // Pirate
      ctx.fillStyle = '#1a1a1a';
      fillPixels(ctx, [[1, 0], [2, 0], [3, 0], [4, 0], [5, 0], [6, 0], [2, 1], [3, 1], [4, 1], [5, 1]]);
      ctx.fillStyle = '#FFD700'; // skull
      fillPixels(ctx, [[3, 0], [4, 0]]);
      break;
    case 6: // Wizard
      ctx.fillStyle = '#4A148C';
      fillPixels(ctx, [[3, 0], [4, 0], [2, 1], [5, 1], [1, 1], [6, 1]]);
      ctx.fillStyle = '#FFD700'; // star
      fillPixels(ctx, [[3, 0]]);
      break;
    case 7: // Cowboy
      ctx.fillStyle = '#8B6914';
      fillPixels(ctx, [[0, 1], [1, 1], [2, 1], [3, 1], [4, 1], [5, 1], [6, 1], [7, 1], [2, 0], [3, 0], [4, 0], [5, 0]]);
      break;
  }
}

function drawGlasses(ctx: CanvasRenderingContext2D, style: number) {
  switch (style) {
    case 0: // Round
      ctx.fillStyle = '#333';
      ctx.strokeStyle = '#333';
      fillPixels(ctx, [[2, 2], [4, 2], [6, 2], [2, 3], [4, 3], [6, 3]]);
      break;
    case 1: // Square
      ctx.fillStyle = '#333';
      fillPixels(ctx, [[2, 2], [3, 2], [4, 2], [5, 2], [6, 2], [2, 3], [6, 3]]);
      break;
    case 2: // Aviator
      ctx.fillStyle = '#996633';
      fillPixels(ctx, [[2, 2], [3, 2], [5, 2], [6, 2], [4, 2]]);
      ctx.fillStyle = '#88CCFF44';
      break;
    case 3: // Monocle
      ctx.fillStyle = '#996633';
      fillPixels(ctx, [[4, 2], [5, 2], [6, 2], [4, 3], [6, 3], [5, 4]]);
      break;
    case 4: // Sunglasses
      ctx.fillStyle = '#1a1a1a';
      fillPixels(ctx, [[2, 2], [3, 2], [4, 2], [5, 2], [6, 2], [2, 3], [3, 3], [5, 3], [6, 3]]);
      break;
  }
}

export function Avatar({ config, emoji, size = 36, border }: AvatarProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hash = useMemo(() => config ? configHash(config) : null, [config]);

  useEffect(() => {
    if (!config || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;

    const source = renderAvatar(config);
    ctx.clearRect(0, 0, size, size);
    ctx.drawImage(source, 0, 0, size, size);
  }, [config, size, hash]);

  if (!config) {
    // Fallback to emoji or initials
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: `${size}px`,
        height: `${size}px`,
        fontSize: `${size * 0.6}px`,
        borderRadius: '50%',
        background: 'linear-gradient(145deg, #FFF, var(--cream))',
        border: border === 'golden' ? '2px solid var(--gold)' : '1px solid var(--border-ornate)',
        flexShrink: 0,
      }}>
        {emoji || '?'}
      </span>
    );
  }

  const borderStyle = border === 'golden'
    ? '2px solid #FFD700'
    : '1px solid var(--border-ornate)';

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '50%',
        border: borderStyle,
        imageRendering: 'pixelated',
        flexShrink: 0,
        background: '#e8dcc8',
      }}
    />
  );
}
